"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { MatchPlayerState, MatchRole, MatchSpinEvent } from "@/lib/multiplayer";

interface UseMatchResult {
  // True once we're subscribed to the channel.
  connected: boolean;
  // True while the other player is present on the channel.
  opponentPresent: boolean;
  // The opponent's latest broadcast snapshot, or null if none yet.
  opponent: MatchPlayerState | null;
  // The opponent's most recent spin, for live spectating.
  opponentSpin: MatchSpinEvent | null;
  // Push our current snapshot to the opponent.
  publish: (state: MatchPlayerState) => void;
  // Broadcast a spin the instant our wheel starts turning.
  publishSpin: (spin: MatchSpinEvent) => void;
}

// Manages a single Supabase Realtime channel for a match: Presence tells us
// whether the opponent is connected, Broadcast carries each side's live
// snapshot. Mirrors the channel lifecycle to the `matchId` — passing null tears
// it down (e.g. solo mode).
export function useMatch(
  matchId: string | null,
  role: MatchRole,
  selfId: string,
  selfName: string,
): UseMatchResult {
  const [connected, setConnected] = useState(false);
  const [opponentPresent, setOpponentPresent] = useState(false);
  const [opponent, setOpponent] = useState<MatchPlayerState | null>(null);
  const [opponentSpin, setOpponentSpin] = useState<MatchSpinEvent | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  // True only once the socket is SUBSCRIBED. Sending before that makes
  // supabase-js fall back to a REST delivery (with a deprecation warning), so
  // we hold sends until we're connected — the subscribe handler re-sends the
  // last cached snapshot anyway.
  const connectedRef = useRef(false);
  // Cache the last snapshot we sent so we can re-send it when the opponent
  // joins late (otherwise they'd see nothing until our next state change).
  const lastSentRef = useRef<MatchPlayerState | null>(null);

  useEffect(() => {
    if (!matchId) return;

    const channel = supabase.channel(`hoav:match:${matchId}`, {
      config: {
        presence: { key: selfId },
        broadcast: { self: false },
      },
    });

    channel.on("broadcast", { event: "state" }, ({ payload }) => {
      setOpponent(payload as MatchPlayerState);
    });

    channel.on("broadcast", { event: "spin" }, ({ payload }) => {
      setOpponentSpin(payload as MatchSpinEvent);
    });

    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState();
      const others = Object.keys(state).filter((k) => k !== selfId);
      const present = others.length > 0;
      setOpponentPresent(present);
      if (!present) {
        setOpponent(null);
        setOpponentSpin(null);
      } else if (lastSentRef.current) {
        // A (re)joining opponent needs our current snapshot immediately.
        channel.send({
          type: "broadcast",
          event: "state",
          payload: lastSentRef.current,
        });
      }
    });

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        connectedRef.current = true;
        setConnected(true);
        await channel.track({ name: selfName, role });
        if (lastSentRef.current) {
          channel.send({
            type: "broadcast",
            event: "state",
            payload: lastSentRef.current,
          });
        }
      }
    });

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
      connectedRef.current = false;
      setConnected(false);
      setOpponentPresent(false);
      setOpponent(null);
      setOpponentSpin(null);
    };
  }, [matchId, role, selfId, selfName]);

  const publish = useCallback((state: MatchPlayerState) => {
    // Always cache so the subscribe handler can flush it once connected.
    lastSentRef.current = state;
    const ch = channelRef.current;
    if (ch && connectedRef.current) {
      ch.send({ type: "broadcast", event: "state", payload: state });
    }
  }, []);

  const publishSpin = useCallback((spin: MatchSpinEvent) => {
    const ch = channelRef.current;
    if (ch && connectedRef.current) {
      ch.send({ type: "broadcast", event: "spin", payload: spin });
    }
  }, []);

  return { connected, opponentPresent, opponent, opponentSpin, publish, publishSpin };
}
